import { WATTS_IN_MW } from '@/constants/units'
import type { PtuVolumes, Volume } from '@/features/bidding/types/bid'
import { BulkFillMode } from '@/features/bidding/types/bid'
import { roundWattsToNearest10KW } from '@/features/bidding/utils/calculations/convertToRoundedMw'
import { decimalAdjust } from '@/features/bidding/utils/calculations/decimalAdjust'

type CalculateBulkFillCapacityProps = {
  bulkMode: BulkFillMode
  ptu: PtuVolumes
  bulkFillValue: number
  needsToRoundDown: boolean
}
export const calculateBulkFillCapacity = ({
  bulkMode,
  ptu,
  bulkFillValue,
  needsToRoundDown = false,
}: CalculateBulkFillCapacityProps) => {
  let newCapacity: number | null = null

  if (bulkMode === BulkFillMode.ABSOLUTE_VALUE) {
    newCapacity = decimalAdjust('round', bulkFillValue * WATTS_IN_MW, -2)
  } else if (bulkMode === BulkFillMode.FORECAST_RATIO) {
    newCapacity = calculateCapacityRatio(bulkFillValue, ptu.forecastedVolume, needsToRoundDown)
  } else if (bulkMode === BulkFillMode.SUGGESTED_RATIO) {
    newCapacity = calculateCapacityRatio(bulkFillValue, ptu.suggestedVolume, needsToRoundDown)
  } else if (bulkMode === BulkFillMode.OFFER_RATIO) {
    newCapacity = calculateCapacityRatio(bulkFillValue, ptu.offeredVolume, needsToRoundDown)
  } else if (bulkMode === BulkFillMode.ACCEPTED_RATIO) {
    newCapacity = calculateCapacityRatio(bulkFillValue, ptu.currentAcceptedVolume, needsToRoundDown)
  } else if (bulkMode === BulkFillMode.OFFERED_CAPACITY_RATIO) {
    newCapacity = calculateCapacityRatio(bulkFillValue, ptu.offeredCapacityVolume, needsToRoundDown)
  } else if (bulkMode === BulkFillMode.ACCEPTED_CAPACITY_RATIO) {
    newCapacity = calculateCapacityRatio(bulkFillValue, ptu.acceptedCapacityVolume, needsToRoundDown)
  }

  return newCapacity !== null ? roundWattsToNearest10KW(newCapacity) : null
}

const calculateCapacityRatio = (bulkFillValue: number, volume: Volume | undefined, needsToRoundDown: boolean) => {
  if (!volume) return null

  const rawCapacity = volume.quantity * (bulkFillValue / 100)

  if (needsToRoundDown) {
    // Round down to the nearest MW => 1_000_000 W
    return Math.floor(rawCapacity / 1_000_000) * 1_000_000
  }

  return rawCapacity
}
